import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Users, BookOpen, Award, Clock, ChevronDown, ChevronUp, Search,
    BarChart3, ArrowLeft, TrendingUp, Target, CheckCircle2, AlertCircle, Timer
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import api from '@/lib/api';

const academyAPI = {
    getAdminDashboard: () => api.get('/academy/admin/dashboard'),
    getActivityReport: (q) => api.get('/academy/admin/activity-report', { params: { q } }),
};

function fmtDate(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
            ' à ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
}

function UserActivityReport() {
    const [q, setQ] = useState('');
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState(null);
    const [error, setError] = useState('');

    const run = async () => {
        if (!q.trim()) return;
        setLoading(true); setError(''); setReport(null);
        try {
            const res = await academyAPI.getActivityReport(q.trim());
            setReport(res.data);
        } catch (e) {
            setError(e.response?.data?.detail || 'Erreur lors de la recherche');
        } finally {
            setLoading(false);
        }
    };

    const buildSummaryText = (r) => {
        const lines = [];
        const roleLabel = ({ apporteur: 'Apporteur', vp: 'VP', admin: 'Admin', admin_full: 'Admin Full', comptable: 'Comptable' })[r.user.role] || r.user.role || '';
        lines.push(`RÉSUMÉ ACTIVITÉ ACADÉMIE — ${r.user.name}${roleLabel ? ' (' + roleLabel + ')' : ''}${r.user.is_deleted ? ' — compte supprimé' : ''}`);
        lines.push('');
        lines.push(`• Premier accès à l'Académie : ${fmtDate(r.academy_first_activity)}`);
        lines.push(`• Dernière activité enregistrée : ${fmtDate(r.last_activity)}`);
        lines.push(`• Temps total passé sur l'Académie : ${r.total_time_label}`);
        lines.push(`• Modules commencés : ${r.modules_started} / ${r.modules_total_published} (modules concernant son rôle)`);
        lines.push(`• Modules terminés : ${r.modules_completed} / ${r.modules_total_published}`);
        lines.push('');
        lines.push('Détail par module :');
        if (!r.modules.length) {
            lines.push('  (aucune activité enregistrée sur aucun module)');
        } else {
            r.modules.forEach(m => {
                lines.push(`  - ${m.title} : ${m.progress_percent}% — ${m.time_spent_label} — ${m.status}` +
                    (m.started_at ? ` (commencé le ${fmtDate(m.started_at)})` : ''));
            });
        }
        return lines.join('\n');
    };

    const copySummary = () => {
        navigator.clipboard.writeText(buildSummaryText(report));
        toast.success('Résumé copié dans le presse-papier');
    };

    return (
        <Card className="border-l-4 border-l-indigo-500" data-testid="academy-activity-report">
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <Users className="w-5 h-5 text-indigo-600" />
                    Rapport d'activité par utilisateur
                </CardTitle>
                <p className="text-xs text-slate-500">
                    Recherchez par nom complet, email ou identifiant. Fonctionne aussi pour les comptes supprimés.
                </p>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                    <Input
                        placeholder="Ex : Pedro Tovar"
                        value={q}
                        onChange={e => setQ(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && run()}
                        data-testid="activity-report-input"
                    />
                    <Button onClick={run} disabled={loading} data-testid="activity-report-search-btn">
                        <Search className="w-4 h-4 mr-1" />
                        {loading ? 'Recherche…' : 'Générer'}
                    </Button>
                </div>

                {error && <p className="text-sm text-red-600" data-testid="activity-report-error">{error}</p>}

                {report && (
                    <div className="space-y-4" data-testid="activity-report-result">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-slate-900">{report.user.name}</span>
                                {report.user.role && (
                                    <Badge variant="secondary" data-testid="activity-report-role">
                                        {({ apporteur: 'Apporteur', vp: 'VP', admin: 'Admin', admin_full: 'Admin Full', comptable: 'Comptable' })[report.user.role] || report.user.role}
                                    </Badge>
                                )}
                                {report.user.is_deleted && (
                                    <Badge variant="outline" className="text-red-600 border-red-300">Compte supprimé</Badge>
                                )}
                                {report.user.email && <span className="text-xs text-slate-500">{report.user.email}</span>}
                            </div>
                            <Button variant="outline" size="sm" onClick={copySummary} data-testid="activity-report-copy-btn">
                                Copier le résumé
                            </Button>
                        </div>

                        {!report.has_any_activity && (
                            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                                Aucune activité Académie enregistrée pour cet utilisateur (il n'a jamais réellement commencé).
                            </div>
                        )}

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="rounded-lg bg-slate-50 p-3">
                                <p className="text-xs text-slate-500">Premier accès</p>
                                <p className="text-sm font-medium">{fmtDate(report.academy_first_activity)}</p>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-3">
                                <p className="text-xs text-slate-500">Dernière activité</p>
                                <p className="text-sm font-medium">{fmtDate(report.last_activity)}</p>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-3">
                                <p className="text-xs text-slate-500">Temps total</p>
                                <p className="text-sm font-medium">{report.total_time_label}</p>
                            </div>
                            <div className="rounded-lg bg-slate-50 p-3">
                                <p className="text-xs text-slate-500">Modules terminés</p>
                                <p className="text-sm font-medium">{report.modules_completed} / {report.modules_total_published}</p>
                            </div>
                        </div>

                        <div className="border rounded-lg divide-y">
                            {report.modules.length === 0 ? (
                                <p className="p-3 text-sm text-slate-400">Aucun module commencé.</p>
                            ) : report.modules.map((m, i) => (
                                <div key={i} className="p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`activity-report-module-${i}`}>
                                    <div className="min-w-[180px]">
                                        <p className="text-sm font-medium text-slate-800">{m.title}</p>
                                        <p className="text-xs text-slate-500">
                                            {m.started_at ? `Commencé le ${fmtDate(m.started_at)}` : 'Jamais commencé'}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-xs text-slate-500 flex items-center gap-1">
                                            <Clock className="w-3.5 h-3.5" /> {m.time_spent_label}
                                        </span>
                                        <div className="w-28"><Progress value={m.progress_percent} /></div>
                                        <Badge variant="outline" className={cn(
                                            m.status === 'Terminé' ? 'text-emerald-600 border-emerald-300' :
                                            m.status === 'Commencé' ? 'text-amber-600 border-amber-300' :
                                            'text-slate-400 border-slate-300'
                                        )}>{m.status}</Badge>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function formatTime(seconds) {
    if (!seconds || seconds <= 0) return '0min';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}min`;
    return `${m}min`;
}

function ScoreGauge({ score, size = 'md' }) {
    const radius = size === 'lg' ? 40 : 28;
    const stroke = size === 'lg' ? 6 : 4;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (score / 100) * circumference;
    const dim = (radius + stroke) * 2;
    
    const color = score >= 80 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';
    
    return (
        <div className="relative inline-flex items-center justify-center">
            <svg width={dim} height={dim} className="-rotate-90">
                <circle cx={radius + stroke} cy={radius + stroke} r={radius} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
                <circle cx={radius + stroke} cy={radius + stroke} r={radius} fill="none" stroke={color} strokeWidth={stroke}
                    strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-700" />
            </svg>
            <span className={cn(
                "absolute font-bold",
                size === 'lg' ? 'text-lg' : 'text-xs',
                score >= 80 ? 'text-emerald-600' : score >= 50 ? 'text-amber-600' : 'text-rose-600'
            )}>
                {score}
            </span>
        </div>
    );
}

function UserRow({ stat, modulesCount }) {
    const [expanded, setExpanded] = useState(false);
    const u = stat.user;
    const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
    const roleLabels = { admin_full: 'Admin', admin: 'Admin', vp: 'VP', apporteur: 'Apporteur', comptable: 'Comptable' };

    return (
        <>
            <tr 
                className="hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-100"
                onClick={() => setExpanded(!expanded)}
                data-testid={`user-row-${u.id}`}
            >
                <td className="p-3">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-white font-bold text-xs">
                            {(u.first_name?.[0] || '') + (u.last_name?.[0] || '')}
                        </div>
                        <div>
                            <p className="font-medium text-slate-900 text-sm">{name}</p>
                            <p className="text-xs text-slate-500">{roleLabels[u.role] || u.role}</p>
                        </div>
                    </div>
                </td>
                <td className="p-3 text-center">
                    <ScoreGauge score={stat.global_score} />
                </td>
                <td className="p-3 text-center">
                    <span className="text-sm font-medium">{stat.modules_completed}/{stat.modules_total ?? modulesCount}</span>
                    <Progress value={stat.completion_rate} className="h-1.5 mt-1 w-16 mx-auto" />
                </td>
                <td className="p-3 text-center">
                    <span className={cn(
                        "text-sm font-semibold",
                        stat.average_quiz_score >= 80 ? 'text-emerald-600' : stat.average_quiz_score >= 50 ? 'text-amber-600' : 'text-rose-600'
                    )}>
                        {stat.average_quiz_score > 0 ? `${stat.average_quiz_score}%` : '—'}
                    </span>
                </td>
                <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-1 text-sm text-slate-600">
                        <Timer className="w-3.5 h-3.5" />
                        {stat.total_time_formatted}
                    </div>
                </td>
                <td className="p-3 text-center">
                    {expanded ? <ChevronUp className="w-4 h-4 mx-auto text-slate-400" /> : <ChevronDown className="w-4 h-4 mx-auto text-slate-400" />}
                </td>
            </tr>
            {expanded && stat.modules_detail?.length > 0 && (
                <tr>
                    <td colSpan={6} className="p-0">
                        <div className="bg-slate-50 px-6 py-3 border-b border-slate-200">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {stat.modules_detail.map((md) => (
                                    <div key={md.module_id} className={cn(
                                        "p-3 rounded-lg border bg-white",
                                        md.quiz_passed ? 'border-emerald-200' : md.quiz_score !== null ? 'border-amber-200' : 'border-slate-200'
                                    )}>
                                        <p className="font-medium text-sm text-slate-800 truncate">{md.module_title}</p>
                                        <div className="flex items-center gap-3 mt-2 text-xs text-slate-600">
                                            <span className="flex items-center gap-1">
                                                <Target className="w-3 h-3" />
                                                {md.progress_percent}%
                                            </span>
                                            {md.quiz_score !== null && (
                                                <span className={cn(
                                                    "flex items-center gap-1 font-semibold",
                                                    md.quiz_passed ? 'text-emerald-600' : 'text-rose-500'
                                                )}>
                                                    <Award className="w-3 h-3" />
                                                    Quiz: {md.quiz_score}%
                                                </span>
                                            )}
                                            <span className="flex items-center gap-1">
                                                <Clock className="w-3 h-3" />
                                                {formatTime(md.time_spent_seconds)}
                                            </span>
                                        </div>
                                        {md.quiz_attempts > 0 && (
                                            <p className="text-xs text-slate-500 mt-1">{md.quiz_attempts} tentative{md.quiz_attempts > 1 ? 's' : ''}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

export default function AcademyAdmin() {
    const navigate = useNavigate();
    const [dashboard, setDashboard] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [activeTab, setActiveTab] = useState('users');

    useEffect(() => {
        loadDashboard();
    }, []);

    const loadDashboard = async () => {
        setLoading(true);
        try {
            const res = await academyAPI.getAdminDashboard();
            setDashboard(res.data);
        } catch (err) {
            toast.error('Erreur chargement du dashboard');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6 p-6">
                    <Skeleton className="h-10 w-64" />
                    <div className="grid grid-cols-4 gap-4">
                        {[1,2,3,4].map(i => <Skeleton key={i} className="h-28" />)}
                    </div>
                    <Skeleton className="h-96" />
                </div>
            </DashboardLayout>
        );
    }

    if (!dashboard) return null;

    const { overview, users_stats, modules_stats } = dashboard;
    const filteredUsers = users_stats.filter(s => {
        if (!search) return true;
        const name = `${s.user.first_name} ${s.user.last_name} ${s.user.email}`.toLowerCase();
        return name.includes(search.toLowerCase());
    });

    return (
        <DashboardLayout>
            <div className="space-y-6 p-4 md:p-6" data-testid="academy-admin-dashboard">
                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => navigate('/academy')} data-testid="back-to-academy">
                            <ArrowLeft className="w-4 h-4 mr-1" />
                            Retour
                        </Button>
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900">Dashboard Formation</h1>
                            <p className="text-sm text-slate-500">Suivi de progression de tous les utilisateurs</p>
                        </div>
                    </div>
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="border-l-4 border-l-blue-500">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-50 rounded-lg"><BookOpen className="w-5 h-5 text-blue-600" /></div>
                                <div>
                                    <p className="text-2xl font-bold">{overview.published_modules}</p>
                                    <p className="text-xs text-slate-500">Modules publiés</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-emerald-500">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-emerald-50 rounded-lg"><Users className="w-5 h-5 text-emerald-600" /></div>
                                <div>
                                    <p className="text-2xl font-bold">{overview.users_with_progress}</p>
                                    <p className="text-xs text-slate-500">Utilisateurs actifs</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-yellow-500">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-yellow-50 rounded-lg"><TrendingUp className="w-5 h-5 text-yellow-600" /></div>
                                <div>
                                    <p className="text-2xl font-bold">
                                        {users_stats.length > 0 ? Math.round(users_stats.reduce((a, b) => a + b.global_score, 0) / users_stats.length) : 0}
                                    </p>
                                    <p className="text-xs text-slate-500">Score moyen /100</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-purple-500">
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-purple-50 rounded-lg"><BarChart3 className="w-5 h-5 text-purple-600" /></div>
                                <div>
                                    <p className="text-2xl font-bold">
                                        {users_stats.length > 0 ? Math.round(users_stats.reduce((a, b) => a + b.average_quiz_score, 0) / users_stats.filter(s => s.average_quiz_score > 0).length || 0) : 0}%
                                    </p>
                                    <p className="text-xs text-slate-500">Moy. quiz</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Rapport d'activité par utilisateur (recherche, fonctionne aussi pour comptes supprimés) */}
                <UserActivityReport />

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        <TabsTrigger value="users" className="gap-2" data-testid="tab-users">
                            <Users className="w-4 h-4" /> Utilisateurs
                        </TabsTrigger>
                        <TabsTrigger value="modules" className="gap-2" data-testid="tab-modules">
                            <BookOpen className="w-4 h-4" /> Modules
                        </TabsTrigger>
                    </TabsList>

                    {/* Tab Utilisateurs */}
                    <TabsContent value="users">
                        <Card>
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between gap-4">
                                    <CardTitle className="text-base">Progression par utilisateur</CardTitle>
                                    <div className="relative w-64">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <Input
                                            value={search}
                                            onChange={e => setSearch(e.target.value)}
                                            placeholder="Rechercher..."
                                            className="pl-9 h-9"
                                            data-testid="search-users"
                                        />
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="p-0">
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
                                                <th className="p-3 text-left font-medium">Utilisateur</th>
                                                <th className="p-3 text-center font-medium">Note /100</th>
                                                <th className="p-3 text-center font-medium">Modules</th>
                                                <th className="p-3 text-center font-medium">Moy. Quiz</th>
                                                <th className="p-3 text-center font-medium">Temps actif</th>
                                                <th className="p-3 text-center font-medium w-12"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredUsers.map(stat => (
                                                <UserRow key={stat.user.id} stat={stat} modulesCount={overview.published_modules} />
                                            ))}
                                            {filteredUsers.length === 0 && (
                                                <tr>
                                                    <td colSpan={6} className="text-center py-8 text-slate-500">
                                                        Aucun résultat
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Tab Modules */}
                    <TabsContent value="modules">
                        <div className="grid gap-4">
                            {modules_stats.map(ms => (
                                <Card key={ms.module.id} data-testid={`module-stat-${ms.module.id}`}>
                                    <CardContent className="p-5">
                                        <div className="flex items-center justify-between flex-wrap gap-4">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <h3 className="font-semibold text-slate-900 truncate">{ms.module.title}</h3>
                                                    <Badge variant={ms.module.is_published ? "default" : "secondary"} className="text-xs shrink-0">
                                                        {ms.module.is_published ? 'Publié' : 'Brouillon'}
                                                    </Badge>
                                                </div>
                                                <p className="text-sm text-slate-500">{ms.module.category}</p>
                                            </div>
                                            <div className="flex items-center gap-6 text-sm">
                                                <div className="text-center">
                                                    <p className="font-bold text-lg text-slate-900">{ms.users_started}</p>
                                                    <p className="text-xs text-slate-500">Commencé</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="font-bold text-lg text-emerald-600">{ms.users_completed}</p>
                                                    <p className="text-xs text-slate-500">Terminé</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="font-bold text-lg text-blue-600">{ms.completion_rate}%</p>
                                                    <p className="text-xs text-slate-500">Taux</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className={cn(
                                                        "font-bold text-lg",
                                                        ms.average_quiz_score >= 80 ? 'text-emerald-600' : ms.average_quiz_score >= 50 ? 'text-amber-600' : 'text-rose-600'
                                                    )}>
                                                        {ms.average_quiz_score > 0 ? `${ms.average_quiz_score}%` : '—'}
                                                    </p>
                                                    <p className="text-xs text-slate-500">Moy. Quiz</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="font-bold text-lg text-slate-700">{formatTime(ms.average_time_seconds)}</p>
                                                    <p className="text-xs text-slate-500">Temps moy.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </DashboardLayout>
    );
}
